import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, Truck, HardHat, Building2, Calendar, FileText, Wrench, Newspaper, Phone, ArrowRight, Layers, Users, Shield, Calculator, Settings, Sparkles, BookOpen, Video, PenTool, Droplets, Construction, FolderOpen, Handshake } from 'lucide-react';
import { Button } from './ui/button';
import MobileCraneIcon from './icons/MobileCraneIcon';

const LOGO_ICON = "https://equiprwebsitestorage.blob.core.windows.net/website-assets/EQUIPR%20Icon300X300px.png";
const LOGO_FULL = "https://equiprwebsitestorage.blob.core.windows.net/website-assets/EQUIPR-Logo_Rev.png";

// Featured article for the dropdown
const featuredArticle = {
  image: 'https://equiprwebsitestorage.blob.core.windows.net/website-assets/Borger%20website.jpg',
  title: 'Borger Cranes: How a Partnership Forged in the Field Helped Shape the Future of Crane and Rigging Software',
  link: '/news/borger-cranes-partnership-future-crane-rigging-software'
};

const navItems = [
  {
    label: 'Solutions',
    description: 'Comprehensive software modules designed to power every aspect of your hire and operations business.',
    overviewLink: '/plant-hire-software',
    submenu: [
      { label: 'CORE', href: '/core', icon: Layers, description: 'SaaS Operations Management' },
      { label: 'Civil', href: '/civil', icon: Building2, description: 'Project-centric field operations' },
      { label: 'Horizon', href: '/horizon', icon: Layers, description: 'Heavy lift & shift planning' },
      { label: 'Allocate', href: '/allocations-scheduling-operated-plant-hire', icon: Calendar, description: 'Scheduling & dispatch' },
      { label: 'Operator', href: '/operator-mobile-dockets-timesheets', icon: FileText, description: 'Mobile dockets & timesheets' },
      { label: 'Guardian', href: '/guardian', icon: Shield, description: 'Asset Maintenance, Safety & Compliance' },
      { label: 'EBA', href: '/eba', icon: Calculator, description: 'Award interpretation', comingSoon: true },
      { label: 'Custom', href: '/custom-solutions', icon: Settings, description: 'Tailored solutions' },
    ]
  },
  {
    label: 'Industries',
    description: 'Purpose-built solutions for your specific industry, designed by experts who understand your challenges.',
    overviewLink: '/plant-hire-software',
    submenu: [
      { label: 'Plant Hire', href: '/plant-hire-software', icon: HardHat, description: 'Operated equipment hire' },
      { label: 'Mobile Cranes', href: '/mobile-crane-hire-rigging', icon: MobileCraneIcon, description: 'Crane & rigging operations' },
      { label: 'Truck / Cartage', href: '/truck-cartage-software', icon: Truck, description: 'Transport & haulage' },
      { label: 'Road Sweeping', href: '/road-sweeping-software', icon: Droplets, description: 'Sweeping & cleaning services' },
      { label: 'Concrete Pumping', href: '/concrete-pumping-software', icon: Construction, description: 'Concrete delivery & pumping' },
      { label: 'Civil', href: '/civil-construction-operations-software', icon: Building2, description: 'Civil construction' },
      { label: 'Yours', href: '/your-industry', icon: Sparkles, description: 'Custom industry solution' },
    ]
  },
  {
    label: 'Features',
    description: 'Powerful tools designed to help you plan better and work better every day.',
    overviewLink: '/allocations-scheduling-operated-plant-hire',
    submenu: [
      { label: 'Allocations & Scheduling', href: '/allocations-scheduling-operated-plant-hire', icon: Calendar, description: 'Resource planning & dispatch' },
      { label: 'Mobile Dockets & Timesheets', href: '/operator-mobile-dockets-timesheets', icon: FileText, description: 'Digital field documentation' },
      { label: 'Compliance & Safety', href: '/compliance-safety-management', icon: Shield, description: 'Real-time compliance visibility' },
      { label: 'Servicing & Maintenance', href: '/servicing-and-maintenance-software', icon: Wrench, description: 'Equipment maintenance tracking' },
    ]
  },
  {
    label: 'Resources',
    description: 'Insights, guides, and educational content to help you get the most out of EQUIPR.',
    overviewLink: '/news',
    submenu: [
      { label: 'News', href: '/news#news', icon: Newspaper, description: 'Latest updates & announcements' },
      { label: 'Guides', href: '/news#news', icon: BookOpen, description: 'How-to guides & tutorials' },
      { label: 'Blog', href: '/news#news', icon: PenTool, description: 'Industry insights & tips' },
      { label: 'Webinars', href: '/news#news', icon: Video, description: 'Live & recorded sessions' },
      { label: 'Brochures', href: '/news#brochures', icon: FolderOpen, description: 'Product brochures & downloads' },
    ]
  },
  { label: 'Contact', href: '/contact-us', icon: Phone },
  {
    label: 'Company',
    submenu: [
      { label: 'About Us', href: '/about', icon: Building2, description: 'Our story and mission' },
      { label: 'Case Studies', href: '/case-studies', icon: Users, description: 'Customer success stories' },
      { label: 'Partners', href: '/preferred-partners', icon: Handshake, description: 'Preferred industry partners' },
      { label: 'Legal & Trust', href: '/legal', icon: Shield, description: 'Policies and security' },
    ],
  },
];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location]);

  const handleDropdownEnter = (index) => {
    if (navItems[index].submenu) {
      setActiveDropdown(index);
    }
  };

  const handleDropdownLeave = () => {
    setActiveDropdown(null);
  };

  return (
    <header 
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled ? 'bg-equipr-dark/95 backdrop-blur-md shadow-lg' : 'bg-equipr-dark'
      }`}
      data-testid="main-header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="header-logo">
            <img src={LOGO_ICON} alt="EQUIPR" className="h-10 w-10 md:hidden" />
            <img src={LOGO_FULL} alt="EQUIPR Software" className="h-8 hidden md:block" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {navItems.map((item, index) => (
              <div 
                key={index} 
                className="relative"
                onMouseEnter={() => handleDropdownEnter(index)}
                onMouseLeave={handleDropdownLeave}
              >
                {item.submenu ? (
                  <>
                    <button 
                      className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium uppercase tracking-wider transition-colors rounded-md ${
                        activeDropdown === index 
                          ? 'text-equipr-yellow bg-white/5' 
                          : 'text-white/80 hover:text-white hover:bg-white/5'
                      }`}
                      data-testid={`nav-dropdown-${item.label.toLowerCase().replace(' ', '-')}`}
                    >
                      {item.label}
                      <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${activeDropdown === index ? 'rotate-180' : ''}`} />
                    </button>
                    <AnimatePresence>
                      {activeDropdown === index && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          transition={{ duration: 0.2 }}
                          className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-white rounded-xl overflow-hidden shadow-2xl border border-gray-100"
                          style={{ width: item.submenu.length > 4 ? '820px' : '720px' }}
                        >
                          <div className="grid grid-cols-12 gap-0">
                            {/* Left Column - Description */}
                            <div className="col-span-3 bg-gray-50 p-6 border-r border-gray-100">
                              <h3 className="font-heading text-lg font-bold text-equipr-dark uppercase mb-2">
                                {item.label}
                              </h3>
                              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                                {item.description}
                              </p>
                              <Link
                                to={item.overviewLink}
                                className="inline-flex items-center gap-2 text-sm font-semibold text-equipr-dark hover:text-equipr-yellow transition-colors group"
                              >
                                Overview
                                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                              </Link>
                            </div>

                            {/* Middle Column - Menu Items (2 columns for many items) */}
                            <div className={`${item.submenu.length > 4 ? 'col-span-6' : 'col-span-5'} p-6`}>
                              <div className={`grid ${item.submenu.length > 4 ? 'grid-cols-2' : 'grid-cols-1'} gap-1`}>
                                {item.submenu.map((subItem, subIndex) => (
                                  <Link
                                    key={subIndex}
                                    to={subItem.href}
                                    className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors group"
                                    data-testid={`nav-link-${subItem.label.toLowerCase().replace(/\s+/g, '-')}`}
                                  >
                                    <div className="w-9 h-9 bg-equipr-yellow/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-equipr-yellow/20 transition-colors">
                                      <subItem.icon className="h-4 w-4 text-equipr-dark" />
                                    </div>
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2">
                                        <span className="font-semibold text-equipr-dark text-sm group-hover:text-equipr-yellow transition-colors">
                                          {subItem.label}
                                        </span>
                                        {subItem.comingSoon && (
                                          <span className="px-1.5 py-0.5 bg-equipr-yellow text-equipr-dark text-[10px] font-bold uppercase rounded tracking-wide">
                                            Coming Soon
                                          </span>
                                        )}
                                        {subItem.earlyAdopter && (
                                          <span className="px-1.5 py-0.5 bg-equipr-yellow text-equipr-dark text-[10px] font-bold uppercase rounded tracking-wide">
                                            Early Adopter
                                          </span>
                                        )}
                                      </div>
                                      <div className="text-xs text-gray-500 mt-0.5 leading-tight">
                                        {subItem.description}
                                      </div>
                                    </div>
                                  </Link>
                                ))}
                              </div>
                            </div>

                            {/* Right Column - Featured Article */}
                            <div className={`${item.submenu.length > 4 ? 'col-span-3' : 'col-span-4'} p-6 bg-gradient-to-br from-gray-50 to-white`}>
                              <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                                Featured
                              </div>
                              <Link to={featuredArticle.link} className="block group">
                                <div className="rounded-lg overflow-hidden mb-3">
                                  <img 
                                    src={featuredArticle.image} 
                                    alt="Featured article"
                                    className="w-full h-28 object-cover group-hover:scale-105 transition-transform duration-300"
                                  />
                                </div>
                                <h4 className="text-sm font-semibold text-equipr-dark group-hover:text-equipr-yellow transition-colors leading-snug">
                                  {featuredArticle.title}
                                </h4>
                                <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 mt-2 group-hover:text-equipr-yellow transition-colors">
                                  Read article <ArrowRight className="h-3 w-3" />
                                </span>
                              </Link>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </>
                ) : (
                  <Link
                    to={item.href}
                    className={`px-4 py-2 text-sm font-medium uppercase tracking-wider transition-colors rounded-md ${
                      location.pathname === item.href 
                        ? 'text-equipr-yellow bg-white/5' 
                        : 'text-white/80 hover:text-white hover:bg-white/5'
                    }`}
                    data-testid={`nav-link-${item.label.toLowerCase()}`}
                  >
                    {item.label}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <Link to={location.pathname === '/core' ? '/early-adopter' : '/book-a-demo'}>
              <Button 
                className="bg-equipr-yellow text-black hover:bg-equipr-yellow-hover font-bold uppercase tracking-wide px-6 py-2 btn-primary-glow"
                data-testid="header-demo-btn"
              >
                {'Request Demo'}
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-white p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-equipr-dark border-t border-white/5"
            data-testid="mobile-menu"
          >
            <nav className="px-6 py-4">
              {navItems.map((item, index) => (
                <div key={index} className="py-2">
                  {item.submenu ? (
                    <>
                      <button
                        onClick={() => setActiveDropdown(activeDropdown === index ? null : index)}
                        className="w-full flex items-center justify-between text-white/80 py-2"
                      >
                        <span className="uppercase tracking-wider text-sm font-medium">{item.label}</span>
                        <ChevronDown className={`h-4 w-4 transition-transform ${activeDropdown === index ? 'rotate-180' : ''}`} />
                      </button>
                      <AnimatePresence>
                        {activeDropdown === index && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="pl-4 border-l-2 border-equipr-yellow/30 ml-2 mt-2"
                          >
                            {item.submenu.map((subItem, subIndex) => (
                              <Link
                                key={subIndex}
                                to={subItem.href}
                                className="flex items-center gap-3 py-3 text-sm text-white/70 hover:text-equipr-yellow"
                              >
                                <subItem.icon className="h-4 w-4" />
                                <span>{subItem.label}</span>
                                {subItem.comingSoon && (
                                  <span className="px-1.5 py-0.5 bg-equipr-yellow text-equipr-dark text-[9px] font-bold uppercase rounded tracking-wide">
                                    Soon
                                  </span>
                                )}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </>
                  ) : (
                    <Link
                      to={item.href}
                      className={`block py-2 uppercase tracking-wider text-sm font-medium ${
                        location.pathname === item.href ? 'text-equipr-yellow' : 'text-white/80'
                      }`}
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              ))}
              <Link to={location.pathname === '/core' ? '/early-adopter' : '/book-a-demo'} className="block mt-4">
                <Button className="w-full bg-equipr-yellow text-equipr-dark hover:bg-equipr-yellow-hover font-bold uppercase tracking-wide">
                  {'Request Demo'}
                </Button>
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
